import type { Language } from '@/i18n';

export function heroCarouselCopy(locale: Language) {
  if (locale === 'fi')
    return {
      carouselPlayLabel: 'Käynnistä esitys',
      carouselPauseLabel: 'Pysäytä esitys',
    };
  if (locale === 'sv')
    return {
      carouselPlayLabel: 'Starta bildspelet',
      carouselPauseLabel: 'Pausa bildspelet',
    };
  if (locale === 'es')
    return {
      carouselPlayLabel: 'Reproducir carrusel',
      carouselPauseLabel: 'Pausar carrusel',
    };
  return {
    carouselPlayLabel: 'Play carousel',
    carouselPauseLabel: 'Pause carousel',
  };
}

export function heroSlideCopy(locale: Language) {
  if (locale === 'fi')
    return {
      growth: {
        title: 'Näe metsäsi tulevaisuus.',
        summary: 'Näe, miten puuston määrä muuttuu suunnitelman eri vaiheissa.',
        proofPoints: ['Puuston kehitys', 'Suunnitelman vaikutukset', 'Oman metsän tiedot'] as const,
      },
      choices: {
        title: 'Vertaa ennen kuin päätät.',
        summary: 'Näe, mitä eri tavoitteet merkitsevät metsäsi arvolle ja tuotoille.',
        proofPoints: ['Omat tavoitteesi', 'Vaihtoehdot euroina', 'Riippumaton päätöstuki'] as const,
      },
      'harvest-income': {
        title: 'Myydäkö nyt vai odottaa?',
        summary: 'Näe alueesi puun hintakehitys ja arvioi, milloin sinun kannattaa myydä.',
        proofPoints: ['Oman alueesi hinnat', 'Hintojen suunta', 'Kolmen vuoden kehitys'] as const,
      },
    };
  if (locale === 'sv')
    return {
      growth: {
        title: 'Se din skogs framtid.',
        summary: 'Se hur virkesvolymen förändras under planens olika skeden.',
        proofPoints: ['Virkesutveckling', 'Planens effekter', 'Din skogs data'] as const,
      },
      choices: {
        title: 'Jämför innan du bestämmer.',
        summary: 'Se vad olika mål innebär för din skogs värde och avkastning.',
        proofPoints: ['Dina mål', 'Alternativ i euro', 'Oberoende beslutsstöd'] as const,
      },
      'harvest-income': {
        title: 'Sälja nu eller vänta?',
        summary:
          'Se virkesprisernas utveckling i din region och bedöm när det kan vara rätt för dig att sälja.',
        proofPoints: ['Priser i din region', 'Prisernas riktning', 'Tre års utveckling'] as const,
      },
    };
  if (locale === 'es')
    return {
      growth: {
        title: 'Mira el futuro de tu bosque.',
        summary: 'Observa cómo cambia el volumen en las distintas fases del plan.',
        proofPoints: ['Evolución del bosque', 'Efectos del plan', 'Datos de tu bosque'] as const,
      },
      choices: {
        title: 'Compara antes de decidir.',
        summary:
          'Descubre qué significan los distintos objetivos para el valor y el rendimiento de tu bosque.',
        proofPoints: [
          'Tus objetivos',
          'Alternativas en euros',
          'Apoyo independiente para decidir',
        ] as const,
      },
      'harvest-income': {
        title: '¿Vender ahora o esperar?',
        summary:
          'Consulta la evolución de los precios de la madera en tu región y valora cuándo te conviene vender.',
        proofPoints: [
          'Precios de tu región',
          'Tendencia de los precios',
          'Evolución de tres años',
        ] as const,
      },
    };
  return {
    growth: {
      title: 'See your forest’s future.',
      summary: 'See how standing volume changes through the plan’s stages.',
      proofPoints: ['Forest development', 'Plan effects', 'Your forest data'] as const,
    },
    choices: {
      title: 'Compare before you decide.',
      summary: 'See what different goals mean for your forest’s value and returns.',
      proofPoints: ['Your goals', 'Options in euros', 'Independent decision support'] as const,
    },
    'harvest-income': {
      title: 'Sell now or wait?',
      summary:
        'See how timber prices have developed in your region and assess when it may make sense for you to sell.',
      proofPoints: ['Prices in your region', 'Price direction', 'Three-year trend'] as const,
    },
  };
}

export function heroForecastTableCopy(locale: Language) {
  if (locale === 'fi')
    return {
      choicesValueLabel: 'Koko jakson arvo',
      choicesNearTermLabel: 'Lähivuosien tulot',
      choicesAvoidClearfellLabel: 'Vältä avohakkuuta',
      choicesTapioLabel: 'Noudata Tapion suosituksia',
      choicesPlanLabel: 'Tavoite',
      choicesNpvLabel: '€/ha',
      choicesNetLabel: 'Nettotuotto',
      choicesTableCaption: 'Vertaa tavoitteiden vaikutuksia',
      unavailableValueLabel: 'Ei saatavilla',
    };
  if (locale === 'sv')
    return {
      choicesValueLabel: 'Värde över hela perioden',
      choicesNearTermLabel: 'Inkomster de närmaste åren',
      choicesAvoidClearfellLabel: 'Undvik kalavverkning',
      choicesTapioLabel: 'Följ Tapios rekommendationer',
      choicesPlanLabel: 'Mål',
      choicesNpvLabel: '€/ha',
      choicesNetLabel: 'Nettointäkt',
      choicesTableCaption: 'Jämför målens effekter',
      unavailableValueLabel: 'Inte tillgängligt',
    };
  if (locale === 'es')
    return {
      choicesValueLabel: 'Valor de todo el periodo',
      choicesNearTermLabel: 'Ingresos próximos',
      choicesAvoidClearfellLabel: 'Evitar talas rasas',
      choicesTapioLabel: 'Seguir las recomendaciones de Tapio',
      choicesPlanLabel: 'Objetivo',
      choicesNpvLabel: '€/ha',
      choicesNetLabel: 'Ingreso neto',
      choicesTableCaption: 'Compara los efectos de los objetivos',
      unavailableValueLabel: 'No disponible',
    };
  return {
    choicesValueLabel: 'Whole-period value',
    choicesNearTermLabel: 'Near-term income',
    choicesAvoidClearfellLabel: 'Avoid clearfelling',
    choicesTapioLabel: 'Follow Tapio recommendations',
    choicesPlanLabel: 'Objective',
    choicesNpvLabel: '€/ha',
    choicesNetLabel: 'Net return',
    choicesTableCaption: 'Compare the effects of each objective',
    unavailableValueLabel: 'Unavailable',
  };
}
